import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { DataLabel, fmtDate, fmtDateTime, daysUntil, urgencyClass } from "@/lib/utils-ui";
import { toast } from "sonner";
import { Send, Package, LifeBuoy, ArrowRight } from "lucide-react";

const CATEGORIES = ["it_issue","asset_request","repair_request","toner_request","access_request","other"];
const PRIORITIES = ["low","medium","high","critical"];

export default function EmployeePortalPage() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [tickets, setTickets] = useState([]);
  const [assets, setAssets] = useState([]);
  const [form, setForm] = useState({ title: "", description: "", category: "it_issue", priority: "medium" });

  const load = async () => {
    const [t, a] = await Promise.all([
      api.get("/tickets"),
      api.get("/assets").catch(() => ({ data: [] })),
    ]);
    setTickets(t.data);
    setAssets((a.data || []).filter(x => x.assigned_user_id === user.id));
  };
  useEffect(() => { load(); }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.title.trim() || !form.description.trim()) { toast.error("Title and description required"); return; }
    try {
      await api.post("/tickets", form);
      toast.success("Ticket submitted — IT will respond shortly");
      setForm({ title: "", description: "", category: "it_issue", priority: "medium" });
      load();
    } catch (err) { toast.error(err?.response?.data?.detail || "Failed to submit"); }
  };

  const openStatuses = ["open","assigned","in_progress"];
  const open = tickets.filter(t => openStatuses.includes(t.status));
  const closed = tickets.filter(t => !openStatuses.includes(t.status));

  return (
    <div className="p-8 space-y-6" data-testid="employee-portal">
      <div>
        <DataLabel>Employee Portal · {user.company_id ? "Company workspace" : "Workspace"}</DataLabel>
        <h1 className="font-head font-black uppercase text-4xl tracking-tighter mt-1">
          Hello, <span className="text-[#FF5000]">{user.full_name?.split(" ")[0]}</span>.
        </h1>
        <p className="text-sm text-muted-foreground mt-1">Raise a ticket, track progress, see your assigned kit.</p>
      </div>

      {/* Summary strip */}
      <div className="grid grid-cols-1 md:grid-cols-3 border border-border">
        <div className="p-5 border-r border-border bg-white" data-testid="summary-open-tickets">
          <DataLabel>Open Tickets</DataLabel>
          <div className="font-head font-black text-4xl tracking-tighter mt-1">{open.length}</div>
          <div className="text-xs text-muted-foreground mt-1">Awaiting IT response</div>
        </div>
        <div className="p-5 border-r border-border bg-white" data-testid="summary-resolved">
          <DataLabel>Resolved / Closed</DataLabel>
          <div className="font-head font-black text-4xl tracking-tighter mt-1">{closed.length}</div>
        </div>
        <div className="p-5 bg-white" data-testid="summary-my-assets">
          <DataLabel>Assigned Assets</DataLabel>
          <div className="font-head font-black text-4xl tracking-tighter mt-1">{assets.length}</div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-5 gap-0 border border-border bg-white">
        {/* Submit request */}
        <div className="lg:col-span-2 border-r border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <LifeBuoy size={16} className="text-[#FF5000]"/>
            <div className="font-head font-bold uppercase text-sm tracking-wider">Submit a request</div>
          </div>
          <form onSubmit={submit} className="space-y-3" data-testid="portal-ticket-form">
            <div><Label className="data-label">Title</Label>
              <Input data-testid="portal-title" value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Short summary"/></div>
            <div><Label className="data-label">Description</Label>
              <Textarea data-testid="portal-desc" rows={4} value={form.description} onChange={e => setForm({...form, description: e.target.value})} placeholder="Tell IT what's happening…"/></div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label className="data-label">Category</Label>
                <Select value={form.category} onValueChange={(v) => setForm({...form, category: v})}>
                  <SelectTrigger data-testid="portal-category"><SelectValue/></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c.replace("_"," ")}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div><Label className="data-label">Priority</Label>
                <Select value={form.priority} onValueChange={(v) => setForm({...form, priority: v})}>
                  <SelectTrigger data-testid="portal-priority"><SelectValue/></SelectTrigger>
                  <SelectContent>{PRIORITIES.map(p => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <Button type="submit" data-testid="portal-submit" className="w-full bg-black text-white hover:bg-[#FF5000] h-10 uppercase tracking-wider text-xs font-head"><Send size={14} className="mr-1"/>Submit ticket</Button>
          </form>
        </div>

        {/* My tickets */}
        <div className="lg:col-span-3 p-6">
          <div className="font-head font-bold uppercase text-sm tracking-wider mb-4">My tickets</div>
          <div className="divide-y divide-border" data-testid="my-tickets-list">
            {tickets.length === 0 && <div className="text-xs text-muted-foreground py-6">No tickets yet. Submit one on the left.</div>}
            {tickets.slice(0, 12).map(t => (
              <button key={t.id} onClick={() => nav(`/tickets/${t.id}`)} data-testid={`my-ticket-${t.id}`}
                      className="w-full text-left py-3 flex items-center justify-between hover:bg-muted px-2 -mx-2">
                <div className="min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-[10px] text-muted-foreground">{t.ticket_id}</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 font-semibold status-${t.status}`}>{t.status.replace("_"," ")}</span>
                    <span className={`text-[10px] uppercase px-1.5 py-0.5 font-semibold priority-${t.priority}`}>{t.priority}</span>
                  </div>
                  <div className="font-semibold text-sm mt-1 truncate">{t.title}</div>
                  <div className="font-mono text-[10px] text-muted-foreground mt-0.5">Opened {fmtDateTime(t.created_at)}</div>
                </div>
                <ArrowRight size={14} className="text-muted-foreground shrink-0"/>
              </button>
            ))}
          </div>
        </div>
      </div>

      {/* My assets */}
      <div className="border border-border bg-white">
        <div className="p-4 border-b border-border flex items-center gap-2">
          <Package size={14} className="text-[#FF5000]"/>
          <div className="font-head font-bold uppercase text-sm tracking-wider">Assigned to me</div>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b border-border bg-muted">{["ASSET ID","TYPE","SERIAL","BRAND / MODEL","LOCATION","WARRANTY"].map(h => <th key={h} className="text-left p-3 data-label">{h}</th>)}</tr></thead>
            <tbody>
              {assets.length === 0 && <tr><td colSpan={6} className="p-6 text-center text-xs text-muted-foreground">No assets assigned to you yet.</td></tr>}
              {assets.map(a => {
                const wd = daysUntil(a.warranty_expiry);
                return (
                  <tr key={a.id} className="border-b border-border hover-row" data-testid={`my-asset-${a.id}`}>
                    <td className="p-3 font-mono text-xs">{a.asset_id}</td>
                    <td className="p-3 uppercase text-xs">{a.type}</td>
                    <td className="p-3 font-mono text-xs">{a.serial_number}</td>
                    <td className="p-3">{a.brand} <span className="text-muted-foreground">{a.model}</span></td>
                    <td className="p-3 text-xs">{a.location || "—"}</td>
                    <td className="p-3 font-mono text-xs">{a.warranty_expiry ? <span className={urgencyClass(wd)}>{fmtDate(a.warranty_expiry)}</span> : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
